import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useReportExtraction } from "../../hooks/useReportExtraction";
import { LoadingSpinner } from "../../components/ui/loadingSpinner/loadingSpinner";
import ResizableSplitView from "../extraction/viewExtraction/ResizeableSplitView";
import ExtractionFileViewer, {
  BoundingBox,
} from "../extraction/viewExtraction/ExtractionFileViewer";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  ReportExtractionItemType,
  ReportExtractionItemCitationType,
} from "../../models/reportExtraction";
import { ReportExtractionCards } from "./components/ReportExtractionCards";
import { ReportExtractionTable } from "./components/ReportExtractionTable";
import { ReportExtractionSummaryCard } from "./components/ReportExtractionSummaryCard";
import { Soc1DocumentHeader } from "./components/Soc1DocumentHeader";
import {
  SOC1_SECTION_ORDER,
  SOC1_SCALAR_LABELS,
  SOC1_TAB_LABELS,
  TAB_A_SUMMARY_KEY,
  titleCaseFromSnake,
} from "../../lib/soc1TabLabels";
import { pickCitationForField } from "../../lib/pickCitationForField";
import { useSoc1ExtractionWorkpaperEnabled } from "../../hooks/useSoc1ExtractionWorkpaperEnabled";

const VIEW_PREFERENCE_KEY = "report-extraction-view-mode";

function parseBoundingBox(str: string | null): BoundingBox | null {
  if (!str) return null;
  const matches = str.match(/-?\d+\.?\d*/g);
  if (!matches || matches.length !== 8) return null;
  const nums = matches.map(Number);
  return [
    [nums[0], nums[1]],
    [nums[2], nums[3]],
    [nums[4], nums[5]],
    [nums[6], nums[7]],
  ];
}

export const ViewReportExtractionPage = () => {
  const { jobId } = useParams();
  const workpaperEnabled = useSoc1ExtractionWorkpaperEnabled();
  const {
    job,
    items,
    schemaStructure,
    loading,
    signedUrl,
    deleteItem,
    downloadItems,
    downloadingItems,
  } = useReportExtraction({
    jobId: jobId || "",
    fetchOnLoad: true,
  });

  const handleDeleteItem = useCallback(
    async (itemId: string) => {
      await deleteItem(itemId);
    },
    [deleteItem],
  );

  const [viewMode, setViewMode] = useState<"table" | "cards">(() => {
    const saved = localStorage.getItem(VIEW_PREFERENCE_KEY);
    return saved === "cards" ? "cards" : "table";
  });

  const [selectedCitation, setSelectedCitation] = useState<{
    boundingBox?: BoundingBox;
    pageWidth?: number;
    pageHeight?: number;
    initialPage?: number;
  } | null>(null);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(VIEW_PREFERENCE_KEY, viewMode);
  }, [viewMode]);

  const handleCitationClick = useCallback(
    (citation: ReportExtractionItemCitationType) => {
      const boundingBox = parseBoundingBox(citation.boundingBox);
      setSelectedCitation({
        boundingBox: boundingBox ?? undefined,
        pageWidth: citation.pageWidth ?? undefined,
        pageHeight: citation.pageHeight ?? undefined,
        initialPage: citation.pageNumber ?? undefined,
      });
    },
    [],
  );

  const handleItemClick = useCallback(
    (item: ReportExtractionItemType) => {
      if (item.citations.length > 0) {
        handleCitationClick(item.citations[0]);
      }
    },
    [handleCitationClick],
  );

  // Route a cell click to the citation that sourced the clicked field.
  // See ``pickCitationForField`` for the fieldName lookup + heuristic.
  const handleItemCellClick = useCallback(
    (item: ReportExtractionItemType, fieldName: string | null) => {
      const citation = pickCitationForField(item, fieldName);
      if (citation !== null) handleCitationClick(citation);
    },
    [handleCitationClick],
  );

  const formatValue = useCallback((value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "string" || typeof value === "number")
      return String(value);
    if (Array.isArray(value)) {
      const parts: string[] = value.map((element) => String(element));
      return parts.join(", ");
    }
    try {
      return JSON.stringify(value) ?? "";
    } catch {
      return "";
    }
  }, []);

  const groupedData = useMemo(() => {
    if (!schemaStructure) return null;

    const itemsByKey = new Map<string, ReportExtractionItemType[]>();
    for (const item of items) {
      const key = item.itemKey;
      const groupItems = itemsByKey.get(key) ?? [];
      groupItems.push(item);
      itemsByKey.set(key, groupItems);
    }

    const scalarValues = schemaStructure.scalarFields.map((field) => {
      const groupItems = itemsByKey.get(field) ?? [];
      const sortedItems = [...groupItems].sort(
        (a, b) => a.itemOrder - b.itemOrder,
      );
      const item = sortedItems[0];
      if (!item) {
        return { key: field, value: "", citation: undefined };
      }
      const value =
        item.data[field] ??
        (Object.keys(item.data).length === 1
          ? Object.values(item.data)[0]
          : item.data);
      // Use the citation from the same item that supplied the value.
      return { key: field, value, citation: item.citations[0] };
    });

    const sectionTitleFor = (field: string): string =>
      workpaperEnabled
        ? (SOC1_TAB_LABELS[field] ?? titleCaseFromSnake(field))
        : titleCaseFromSnake(field);

    const arrayGroups = schemaStructure.arrayFields.map((field) => {
      const groupItems = itemsByKey.get(field) ?? [];
      return {
        key: field,
        title: sectionTitleFor(field),
        items: [...groupItems].sort((a, b) => a.itemOrder - b.itemOrder),
      };
    });

    const knownKeys = new Set([
      ...schemaStructure.scalarFields,
      ...schemaStructure.arrayFields,
    ]);
    const extraGroups = Array.from(itemsByKey.entries())
      .filter(([key]) => !knownKeys.has(key))
      .map(([key, groupItems]) => ({
        key,
        title: sectionTitleFor(key),
        items: [...groupItems].sort((a, b) => a.itemOrder - b.itemOrder),
      }));

    return {
      scalarValues,
      arrayGroups: [...arrayGroups, ...extraGroups],
    };
  }, [items, schemaStructure, workpaperEnabled]);

  // Pull the report title for the document header. Falls back to the
  // component's own default when the extractor didn't populate it.
  const documentHeaderValues = useMemo(() => {
    const byKey = new Map(
      (groupedData?.scalarValues ?? []).map((s) => [s.key, s.value]),
    );
    const reportName = byKey.get("report_name");
    return {
      reportName:
        typeof reportName === "string" && reportName.length > 0
          ? reportName
          : null,
    };
  }, [groupedData]);

  // Synthesise an array group for Tab A so ``ReportExtractionTable`` can
  // render its scalars with the same layout + interaction as Tabs B-E.
  const tabAGroup = useMemo(() => {
    if (!workpaperEnabled || !groupedData) return null;
    const items: ReportExtractionItemType[] = groupedData.scalarValues.map(
      (scalar, index) => {
        const fieldLabel =
          SOC1_SCALAR_LABELS[scalar.key] ?? titleCaseFromSnake(scalar.key);
        const rawValue = scalar.value;
        const displayValue: unknown =
          typeof rawValue === "boolean"
            ? rawValue
              ? "Yes"
              : "No"
            : (rawValue ?? "");
        // Strip the citation's fieldName so pickCitationForField falls back
        // to citations[0] on click. The synthetic columns here are "field"
        // and "value", which never match the citation's real fieldName
        // (e.g. "report_type"); leaving it in would suppress navigation.
        const scalarCitation = scalar.citation
          ? { ...scalar.citation, fieldName: null }
          : null;
        return {
          id: `${TAB_A_SUMMARY_KEY}:${scalar.key}`,
          itemKey: TAB_A_SUMMARY_KEY,
          itemOrder: index,
          data: {
            field: fieldLabel,
            value: displayValue,
          },
          citations: scalarCitation ? [scalarCitation] : [],
        };
      },
    );
    return {
      key: TAB_A_SUMMARY_KEY,
      title: SOC1_TAB_LABELS[TAB_A_SUMMARY_KEY] ?? "Tab A — Report Review",
      items,
    };
  }, [workpaperEnabled, groupedData]);

  const orderedVisibleArrayGroups = useMemo(() => {
    const rawGroups = groupedData?.arrayGroups ?? [];
    // Hide items whose data is empty so they don't render as blank rows.
    const stripEmptyItems = <G extends { items: ReportExtractionItemType[] }>(
      group: G,
    ): G => ({
      ...group,
      items: group.items.filter((item) => Object.keys(item.data).length > 0),
    });

    const groups = rawGroups.map(stripEmptyItems);

    if (!workpaperEnabled) {
      return groups.filter((g) => g.items.length > 0);
    }

    const workpaperGroups = tabAGroup ? [tabAGroup, ...groups] : groups;

    const orderIndex = new Map(SOC1_SECTION_ORDER.map((key, i) => [key, i]));
    const inOrder = workpaperGroups.filter((g) => orderIndex.has(g.key));
    // Both keys are guaranteed present in orderIndex by the filter above;
    // `?? 0` only exists to satisfy Map.get's `T | undefined` return type.
    inOrder.sort(
      (a, b) => (orderIndex.get(a.key) ?? 0) - (orderIndex.get(b.key) ?? 0),
    );
    const outOfOrder = workpaperGroups.filter((g) => !orderIndex.has(g.key));
    return [...inOrder, ...outOfOrder].filter((g) => g.items.length > 0);
  }, [groupedData, workpaperEnabled, tabAGroup]);

  const useSectionSelect = orderedVisibleArrayGroups.length > 5;

  const activeGroup = useMemo(() => {
    if (!groupedData) return null;
    if (!selectedGroupKey) {
      return orderedVisibleArrayGroups[0] ?? null;
    }
    return (
      orderedVisibleArrayGroups.find(
        (group) => group.key === selectedGroupKey,
      ) ?? null
    );
  }, [groupedData, selectedGroupKey, orderedVisibleArrayGroups]);

  const activeGroupItems = useMemo(() => {
    if (!schemaStructure || !groupedData) return items;
    if (!activeGroup) return [];
    return activeGroup.items;
  }, [activeGroup, groupedData, items, schemaStructure]);

  useEffect(() => {
    if (!schemaStructure || !groupedData) {
      setSelectedGroupKey(null);
      return;
    }

    const firstGroupKey = orderedVisibleArrayGroups[0]?.key ?? null;
    // Guard against the selected key pointing at a group that no longer
    // renders (e.g., drained to empty items) — check against the filtered
    // list, not the raw arrayGroups.
    if (
      !selectedGroupKey ||
      !orderedVisibleArrayGroups.some((group) => group.key === selectedGroupKey)
    ) {
      setSelectedGroupKey(firstGroupKey);
      setSelectedIndex(null);
    }
  }, [
    groupedData,
    schemaStructure,
    selectedGroupKey,
    orderedVisibleArrayGroups,
  ]);

  useEffect(() => {
    if (!schemaStructure) return;
    if (selectedIndex === null) return;
    if (activeGroupItems.length === 0) {
      setSelectedIndex(null);
      return;
    }
    if (selectedIndex >= activeGroupItems.length) {
      setSelectedIndex(activeGroupItems.length - 1);
    }
  }, [activeGroupItems, schemaStructure, selectedIndex]);

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key === "j" || e.key === "ArrowDown") {
        // Move down
        e.preventDefault();
        setSelectedIndex((prev) => {
          const navigableItems = schemaStructure ? activeGroupItems : items;
          if (navigableItems.length === 0) return null;
          const newIndex =
            prev === null ? 0 : Math.min(prev + 1, navigableItems.length - 1);
          if (navigableItems[newIndex]) {
            handleItemClick(navigableItems[newIndex]);
          }
          return newIndex;
        });
      } else if (e.key === "k" || e.key === "ArrowUp") {
        // Move up
        e.preventDefault();
        setSelectedIndex((prev) => {
          const navigableItems = schemaStructure ? activeGroupItems : items;
          if (navigableItems.length === 0) return null;
          const newIndex = prev === null ? 0 : Math.max(prev - 1, 0);
          if (navigableItems[newIndex]) {
            handleItemClick(navigableItems[newIndex]);
          }
          return newIndex;
        });
      } else if (e.key === "[") {
        // Switch to previous group
        if (!schemaStructure || orderedVisibleArrayGroups.length === 0) return;
        e.preventDefault();
        const currentIndex = orderedVisibleArrayGroups.findIndex(
          (group) => group.key === (activeGroup?.key ?? ""),
        );
        const nextIndex =
          currentIndex <= 0
            ? orderedVisibleArrayGroups.length - 1
            : currentIndex - 1;
        const nextGroup = orderedVisibleArrayGroups[nextIndex];
        if (nextGroup) {
          setSelectedGroupKey(nextGroup.key);
          setSelectedIndex(null);
        }
      } else if (e.key === "]") {
        // Switch to next group
        if (!schemaStructure || orderedVisibleArrayGroups.length === 0) return;
        e.preventDefault();
        const currentIndex = orderedVisibleArrayGroups.findIndex(
          (group) => group.key === (activeGroup?.key ?? ""),
        );
        const nextIndex =
          currentIndex === -1 ||
          currentIndex === orderedVisibleArrayGroups.length - 1
            ? 0
            : currentIndex + 1;
        const nextGroup = orderedVisibleArrayGroups[nextIndex];
        if (nextGroup) {
          setSelectedGroupKey(nextGroup.key);
          setSelectedIndex(null);
        }
      } else if (e.key === "1" && !workpaperEnabled) {
        // Switch to Table view (legacy Table/Cards toggle only)
        setViewMode("table");
      } else if (e.key === "2" && !workpaperEnabled) {
        // Switch to Cards view (legacy Table/Cards toggle only)
        setViewMode("cards");
      } else if ((e.key === "x" || e.key === "Delete") && !workpaperEnabled) {
        // Delete selected item (legacy mode only). Workpaper mode has no
        // delete affordance in the redesigned viewer.
        e.preventDefault();
        const navigableItems = schemaStructure ? activeGroupItems : items;
        if (selectedIndex !== null && navigableItems[selectedIndex]) {
          void handleDeleteItem(navigableItems[selectedIndex].id);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    activeGroupItems,
    handleDeleteItem,
    handleItemClick,
    items,
    activeGroup,
    orderedVisibleArrayGroups,
    schemaStructure,
    selectedIndex,
    workpaperEnabled,
  ]);

  const handleFlatItemClick = useCallback(
    (
      item: ReportExtractionItemType,
      index: number | null,
      fieldName: string | null = null,
    ) => {
      if (index !== null) {
        setSelectedIndex(index);
      }
      setSelectedGroupKey(null);
      handleItemCellClick(item, fieldName);
    },
    [handleItemCellClick],
  );

  const handleGroupItemClick = useCallback(
    (
      groupKey: string,
      item: ReportExtractionItemType,
      index: number | null,
      fieldName: string | null = null,
    ) => {
      setSelectedGroupKey(groupKey);
      if (index !== null) {
        setSelectedIndex(index);
      }
      handleItemCellClick(item, fieldName);
    },
    [handleItemCellClick],
  );

  // Filename mirrors the BE's Content-Disposition: derived from the uploaded
  // document so reviewers see a meaningful name
  // (``oci-mar2026_extraction_report.xlsx``) rather than a UUID. Same rule
  // regardless of workpaper flag — BE picks the exporter, filename is
  // consistent either way. One deliberate divergence: the BE ASCII-folds its
  // header value (HTTP headers are latin-1-constrained), while `a.download`
  // has no such limit, so non-ASCII document names keep their full name here.
  const documentStem = (job?.documentName ?? "").replace(/\.pdf$/i, "");
  const downloadFilename = documentStem
    ? `${documentStem}_extraction_report.xlsx`
    : `extraction_report_${jobId}.xlsx`;

  const downloadButton = (
    <Button
      variant="outline"
      loading={downloadingItems}
      disabled={job?.status !== "succeeded"}
      onClick={() => downloadItems(downloadFilename)}
      title={
        job?.status !== "succeeded"
          ? "Download available once extraction succeeds."
          : "Download items as Excel"
      }
    >
      Download
    </Button>
  );

  // Only rendered under !workpaperEnabled below, so no need for a
  // workpaper-mode branch here.
  const summaryCardForCurrentMode = groupedData ? (
    <ReportExtractionSummaryCard
      entries={groupedData.scalarValues}
      formatLabel={titleCaseFromSnake}
      formatValue={formatValue}
    />
  ) : null;

  const sectionSwitcher =
    orderedVisibleArrayGroups.length > 0 ? (
      <div className="group flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-stone-600">Sections</span>
        {useSectionSelect ? (
          <Select
            value={activeGroup ? activeGroup.key : undefined}
            onValueChange={(value) => {
              setSelectedGroupKey(value);
              setSelectedIndex(null);
            }}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select section" />
            </SelectTrigger>
            <SelectContent>
              {orderedVisibleArrayGroups.map((group) => (
                <SelectItem key={group.key} value={group.key}>
                  {group.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Tabs
            value={activeGroup?.key ?? ""}
            onValueChange={(value) => {
              setSelectedGroupKey(value);
              setSelectedIndex(null);
            }}
          >
            <TabsList className="rounded-md overflow-x-auto">
              {orderedVisibleArrayGroups.map((group) => (
                <TabsTrigger
                  key={group.key}
                  value={group.key}
                  className="rounded-md"
                >
                  {group.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
        <span className="pointer-events-none text-xs text-stone-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
          Use <span className="font-medium">[ ]</span> to switch sections
        </span>
      </div>
    ) : null;

  const groupedTableSection =
    schemaStructure && groupedData ? (
      <div className="flex flex-col gap-6">
        {/* Legacy-mode summary card. Workpaper-on renders Tab A as a table
            inside the section switcher below, so this is null there. */}
        {!workpaperEnabled && summaryCardForCurrentMode}
        {orderedVisibleArrayGroups.length > 0 ? (
          <div className="flex flex-col gap-3">
            {sectionSwitcher}
            {activeGroup ? (
              <div className="flex flex-col gap-3">
                <h2 className="text-lg font-semibold text-stone-900">
                  {activeGroup.title}
                </h2>
                <ReportExtractionTable
                  items={activeGroup.items}
                  onCellClick={(item, index, fieldName) =>
                    handleGroupItemClick(
                      activeGroup.key,
                      item,
                      index,
                      fieldName,
                    )
                  }
                  onDeleteItem={workpaperEnabled ? undefined : handleDeleteItem}
                  selectedIndex={selectedIndex}
                />
              </div>
            ) : (
              <div className="text-center text-gray-500 mt-8">
                No extracted items found.
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-gray-500 mt-8">
            No extracted items found.
          </div>
        )}
      </div>
    ) : (
      <ReportExtractionTable
        items={items}
        onCellClick={handleFlatItemClick}
        onDeleteItem={workpaperEnabled ? undefined : handleDeleteItem}
        selectedIndex={selectedIndex}
      />
    );

  const tableContent = (
    <div className="flex flex-col w-full">
      {workpaperEnabled ? (
        <div className="flex flex-col gap-4">
          <Soc1DocumentHeader
            reportName={documentHeaderValues.reportName}
            status={job?.status ?? null}
            actions={downloadButton}
          />
          {groupedTableSection}
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-semibold">Report Extraction</h1>
          </div>
          <Tabs
            value={viewMode}
            onValueChange={(value) =>
              setViewMode(value === "cards" ? "cards" : "table")
            }
            className="w-full"
          >
            <div className="flex items-center justify-between">
              <div className="group flex items-center gap-3">
                <TabsList className="rounded-md">
                  <TabsTrigger value="table" className="rounded-md">
                    Table
                  </TabsTrigger>
                  <TabsTrigger value="cards" className="rounded-md">
                    Cards
                  </TabsTrigger>
                </TabsList>
                <span className="pointer-events-none text-xs text-stone-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
                  Use <span className="font-medium">1/2</span> for views,{" "}
                  <span className="font-medium">J/K</span> for items
                </span>
              </div>
              {downloadButton}
            </div>

            <TabsContent value="table">{groupedTableSection}</TabsContent>

            <TabsContent value="cards">
              {schemaStructure && groupedData ? (
                <div className="flex flex-col gap-6">
                  <ReportExtractionSummaryCard
                    entries={groupedData.scalarValues}
                    formatLabel={titleCaseFromSnake}
                    formatValue={formatValue}
                  />
                  {orderedVisibleArrayGroups.length > 0 ? (
                    <div className="flex flex-col gap-3">
                      <div className="group flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-stone-600">
                          Sections
                        </span>
                        {useSectionSelect ? (
                          <Select
                            value={activeGroup ? activeGroup.key : undefined}
                            onValueChange={(value) => {
                              setSelectedGroupKey(value);
                              setSelectedIndex(null);
                            }}
                          >
                            <SelectTrigger className="w-64">
                              <SelectValue placeholder="Select section" />
                            </SelectTrigger>
                            <SelectContent>
                              {orderedVisibleArrayGroups.map((group) => (
                                <SelectItem key={group.key} value={group.key}>
                                  {group.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Tabs
                            value={activeGroup?.key ?? ""}
                            onValueChange={(value) => {
                              setSelectedGroupKey(value);
                              setSelectedIndex(null);
                            }}
                          >
                            <TabsList className="rounded-md overflow-x-auto">
                              {orderedVisibleArrayGroups.map((group) => (
                                <TabsTrigger
                                  key={group.key}
                                  value={group.key}
                                  className="rounded-md"
                                >
                                  {group.title}
                                </TabsTrigger>
                              ))}
                            </TabsList>
                          </Tabs>
                        )}
                        <span className="pointer-events-none text-xs text-stone-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
                          Use <span className="font-medium">[ ]</span> to switch
                          sections
                        </span>
                      </div>
                      {activeGroup ? (
                        <div className="flex flex-col gap-3">
                          <h2 className="text-lg font-semibold text-stone-900">
                            {activeGroup.title}
                          </h2>
                          <ReportExtractionCards
                            items={activeGroup.items}
                            onItemClick={(item, index) =>
                              handleGroupItemClick(activeGroup.key, item, index)
                            }
                            onCitationClick={handleCitationClick}
                            onDeleteItem={handleDeleteItem}
                            selectedIndex={selectedIndex}
                          />
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 mt-8">
                          No extracted items found.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 mt-8">
                      No extracted items found.
                    </div>
                  )}
                </div>
              ) : (
                <ReportExtractionCards
                  items={items}
                  onItemClick={handleFlatItemClick}
                  onCitationClick={handleCitationClick}
                  onDeleteItem={handleDeleteItem}
                  selectedIndex={selectedIndex}
                />
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );

  const rightContent = (
    <div className="flex flex-col h-full px-4 pt-4">
      {signedUrl && (
        <div className="grow overflow-hidden">
          <ExtractionFileViewer
            signedUrl={signedUrl}
            fileType="pdf"
            initialPage={selectedCitation?.initialPage}
            boundingBox={selectedCitation?.boundingBox}
            pageWidth={selectedCitation?.pageWidth}
            pageHeight={selectedCitation?.pageHeight}
          />
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="h-full w-full overflow-auto">
        <div className="flex flex-col gap-4 sm:p-20 p-6">
          <h1 className="text-2xl font-semibold">Extraction not found</h1>
          <p className="text-sm text-gray-500">
            The report extraction you are looking for does not exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden">
      <ResizableSplitView
        tableContent={tableContent}
        rightContent={rightContent}
      />
    </div>
  );
};
